import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions';
import type { TeamEditFormValues, EditReferenceData } from '../types/teamEdit.types';

export const teamEditService = {
  async getReferenceData(companyId: string, userId: string): Promise<EditReferenceData> {
    await permissionEngine.assert(userId, 'teams.view', { companyId });
    
    const [projectsRes, usersRes, contractsRes] = await Promise.all([
      supabase.from('projects').select('id, name, code').eq('company_id', companyId).eq('is_active', true).order('name'),
      supabase.from('user_profiles').select('id, full_name').eq('company_id', companyId).eq('is_active', true).order('full_name'),
      supabase.from('construction_contracts').select('id, contract_name, contract_code').eq('company_id', companyId).order('contract_name')
    ]);

    if (projectsRes.error) throw projectsRes.error;
    if (usersRes.error) throw usersRes.error;

    return {
      projects: projectsRes.data.map(p => ({ value: p.id, label: `${p.code} - ${p.name}` })),
      users: usersRes.data.map(u => ({ value: u.id, label: u.full_name })),
      contracts: (contractsRes.data || []).map(c => ({ value: c.id, label: `${c.contract_code} - ${c.contract_name}` }))
    };
  },

  async getTeam(id: string, userId: string): Promise<TeamEditFormValues> {
    const { data: teamData, error: tError } = await supabase
      .from('teams')
      .select('company_id')
      .eq('id', id)
      .single();
    
    if (tError) throw tError;
    
    await permissionEngine.assert(userId, 'teams.update', { companyId: teamData.company_id, teamId: id });

    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as TeamEditFormValues;
  },

  async updateTeam(id: string, values: Partial<TeamEditFormValues>, userId: string) {
    const { data: teamData, error: tError } = await supabase
      .from('teams')
      .select('company_id')
      .eq('id', id)
      .single();
    
    if (tError) throw tError;
    
    await permissionEngine.assert(userId, 'teams.update', { companyId: teamData.company_id, teamId: id });

    const { data, error } = await supabase
      .from('teams')
      .update({
        ...values,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};
